import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SlantedReactangle from "@tecumseh/Icons/SlantedReactangle";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasCategoryContext;

export interface CategoryDetailLinkListStyles {
    container?: GridContainerProps;
    categoryName?: InjectableCss;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    slantedLineIcon?: InjectableCss;
}

export const categoryDetailLinkListStyles: CategoryDetailLinkListStyles = {
    container: {
        gap: 0,
        css: css`
            opacity: 1;
            background-color: ${COLORS.background.lighterGrayShade};
            padding: 10px 10px 10px 20px;
            position: relative;
            &::after {
                content: "";
                position: absolute;
                bottom: 0;
                right: 0;
                -webkit-clip-path: polygon(100% 0, 0% 100%, 100% 100%);
                clip-path: polygon(100% 0, 0% 100%, 100% 100%);
                width: 30px;
                height: 30px;
                background-color: ${COLORS.white};
            }
        `,
    },
    categoryName: {
        css: css`
            opacity: 1;
            color: ${getColor("primary")};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
            text-align: left;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 5px;
            display: flex;
            flex-direction: column;
        `,
    },
    subCategoryNameLink: {
        css: css`
            > span {
                opacity: 1;
                color: ${getColor("secondary")}!important;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.smallest};
                font-weight: ${TYPOGRAPHY.weight.base};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                text-align: left;
                margin-top: 3px;
                text-transform: capitalize;
                span[class^="IconWrapper"] {
                    display: none;
                }

                &:hover {
                    color: ${getColor("primary")}!important;
                    font-weight: ${TYPOGRAPHY.weight.baseBold};
                }
            }
            &.activeClass {
                > span {
                    opacity: 1;
                    color: ${getColor("primary")}!important;
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.baseBold};
                    font-style: normal;
                    letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                    text-align: left;
                    text-transform: capitalize;
                    display: flex;
                }
                span[class^="IconWrapper"] {
                    display: block;
                }
            }
        `,
    },
    slantedLineIcon: {
        css: css`
            margin-left: 10px;
            color: ${getColor("primary")};
        `,
    },
};

const styles = categoryDetailLinkListStyles;

const CategoryDetailLinkList = ({ category }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <StyledWrapper>
                <StyledWrapper {...styles.subCategoryNameLinkItem}>
                    <SubCategoryLink subCategoryId={category.id} categoryName={category.name} properties="" />
                </StyledWrapper>
            </StyledWrapper>
        </GridContainer>
    );
};

const mapStateToProps = (
    state: ApplicationState,
    ownProps: { subCategoryId: string; categoryName: string; properties: string },
) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    return {
        catalogPageState,
    };
};

const CategoryLinkView = ({ catalogPageState }: ReturnType<typeof mapStateToProps>) => {
    if (!catalogPageState) {
        return null;
    }
    const dataObj = catalogPageState.value?.properties.categories
        ? JSON.parse(catalogPageState.value.properties.categories)
        : "";
    return dataObj.map((data: any) => (
        // eslint-disable-next-line react/jsx-key
        <Link
            href={data.path}
            {...styles.subCategoryNameLink}
            className={catalogPageState.value?.categoryId === data.id ? "activeClass" : ""}
            data-test-selector={`categoryDetailsLinkListLink_${catalogPageState.id}`}
        >
            {/* {data.name} <Icon src={SlantedReactangle} size={15} {...styles.slantedLineIcon}></Icon> */}
            {data.name} 
        </Link>
    ));
};

const SubCategoryLink = connect(mapStateToProps)(CategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailLinkList),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
